<template>
  <div
    class="!pl-4 cursor-pointer flex flex-row items-center gap-3 !justify-between dropdown_item project_item_row"
    :class="{ selected: isSelected || isDropdownOpen }"
    @click="$emit('click', project)"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="flex flex-row items-center gap-2 truncate flex-1 min-w-0">
      <ThemeImage
        class="size-4"
        :icon="imageNames.PANEL_PROJECT"
        alt="panel_project_icon"
      />

      <a-tooltip :title="isTruncated ? renderEntity(project) : ''" placement="top" color="var(--tooltip-bg)">
        <span ref="textRef" class="text-sm color_666 font-normal truncate">{{
          renderEntity(project)
        }}</span>
      </a-tooltip>
    </div>

    <div
      class="flex flex-row items-center gap-1 project_item_actions"
      :class="{ show: isDropdownOpen || isHovered }"
    >
      <div
        v-for="(action, index) in actions"
        :key="`${action?.id}_action_${index}`"
        @click.stop="handleActionClick(action)"
      >
        <a-dropdown
          v-if="action?.options?.length > 0"
          trigger="click"
          placement="bottomLeft"
          :get-popup-container="(trigger) => trigger.parentElement"
          @visibleChange="handleDropdownChange($event, action.id)"
        >
          <a-tooltip placement="top" color="var(--tooltip-bg)">
            <template #title>
              <span class="text-[13px] font-normal primary_color">{{
                action?.title
              }}</span>
            </template>
            <div
              class="size-4 flex items-center justify-center cursor-pointer !rounded icon_wrapper"
              :class="{ selected: activeDropdown === action.id }"
            >
              <ThemeImage
                class="size-4"
                :icon="action?.icon"
                :alt="`${action?.id}_icon`"
              />
            </div>
          </a-tooltip>

          <template #overlay>
            <a-menu class="dropdown_menu">
              <a-menu-item
                v-for="(option, oIndex) in action?.options"
                :key="`${option?.id}_option_${oIndex}`"
                @click="handleOptionClick(option, project)"
              >
                <div class="flex flex-row items-center gap-2">
                  <ThemeImage
                    class="size-4"
                    :icon="option?.icon"
                    :alt="`${option?.id}_icon`"
                  />
                  <span class="text-sm primary_color">{{ option?.title }}</span>
                </div>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>

        <a-tooltip
          v-else-if="action?.tooltip"
          placement="top"
          color="var(--tooltip-bg)"
        >
          <template #title>
            <span class="text-[13px] font-normal primary_color">{{
              action?.title
            }}</span>
          </template>
          <ThemeImage
            class="size-4"
            :icon="action?.icon"
            :alt="`${action?.id}_icon`"
          />
        </a-tooltip>
      </div>
    </div>

    <ConfirmationModal
      v-model:visible="showDeleteModal"
      title="Delete Project"
      :message="`Are you sure you want to delete ${project?.name} project?`"
      :loading="isDeleting"
      type="delete"
      @confirm="handleDeleteConfirm"
      @cancel="showDeleteModal = false"
      confirm-text="Delete"
      loading-text="Deleting..."
    />

    <AddOrUpdateProjectSection
      v-if="showRenameModal"
      source="project_item_rename"
      :visible="showRenameModal"
      @update:visible="showRenameModal = $event"
      :project="project"
      :space="null"
      mode="edit"
    />
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { renderEntity } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { getProjectItemActions } from "@/src/constants/projectConstants.js";
import { projectEvents } from "@/src/constants/projectConstants.js";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import AddOrUpdateProjectSection from "@/src/components/spaces_components/projects/AddOrUpdateProjectSection.vue";
import ProjectService from "@/src/api/ProjectService.js";
import eventBus from "@/src/utils/eventBus.js";

const router = useRouter();

const props = defineProps({
  project: { type: Object, required: true },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits(["click", "action-click", "option-click"]);

const activeDropdown = ref(null);
const isDropdownOpen = ref(false);
const isHovered = ref(false);
const textRef = ref(null);
const isTruncated = ref(false);
const showDeleteModal = ref(false);
const showRenameModal = ref(false);
const isDeleting = ref(false);

const actions = getProjectItemActions(imageNames, {
  onRename: (project) => {
    activeDropdown.value = null;
    isDropdownOpen.value = false;
    isHovered.value = false;

    showRenameModal.value = true;
  },
  onOpenInNewTab: (project) => {
    activeDropdown.value = null;
    isDropdownOpen.value = false;
    isHovered.value = false;

    const routeData = router.resolve({
      path: `/spaces/projects/tasks`,
      query: {
        space_id: project.space_id,
        project_id: project.id,
      },
    });
    window.open(routeData.href, "_blank");
  },
  onDelete: (project) => {
    activeDropdown.value = null;
    isDropdownOpen.value = false;
    isHovered.value = false;

    showDeleteModal.value = true;
  },
});

const handleActionClick = (action) => {
  try {
    if (!action?.options?.length && action?.action) {
      action.action();
    }
    emit("action-click", action);
  } catch (error) {
    console.error("An error occurred while handling action click: ", error);
  }
};

const handleOptionClick = (option, project) => {
  try {
    if (option?.action) {
      option.action(project);
    }
    emit("option-click", option, project);
  } catch (error) {
    console.error("An error occurred while handling option click: ", error);
  }
};

const handleMouseEnter = () => {
  isHovered.value = true;
  if (textRef.value) {
    isTruncated.value = textRef.value.scrollWidth > textRef.value.clientWidth;
  }
};

const handleMouseLeave = () => {
  isHovered.value = false;
};

const handleDropdownChange = (visible, actionId) => {
  try {
    if (visible) {
      activeDropdown.value = actionId;
      isDropdownOpen.value = true;
    } else {
      if (activeDropdown.value === actionId) {
        activeDropdown.value = null;
        isDropdownOpen.value = false;
      }
    }
  } catch (error) {
    console.error("An error occurred while handling dropdown change: ", error);
  }
};

const handleDeleteConfirm = async () => {
  try {
    isDeleting.value = true;

    const response = await ProjectService.tasks.deleteProject(props.project?.id);
    if (response?.success) {
      message.success(`Project ${props.project?.name} deleted successfully!`);
      showDeleteModal.value = false;
      eventBus.emit("project-manipulated", projectEvents.DELETED);
    }
  } catch (error) {
    console.error("An error occurred while deleting a project: ", error);
    message.error("An error occurred while deleting project!");
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped>
.project_item_actions {
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
}

.project_item_actions.show {
  opacity: 1;
  visibility: visible;
}
</style>
